import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import toast from "react-hot-toast";
import {
  getProfileDetails,
  updateProfile,
  uploadProfileImage,
  updatePassword,
  tenantLogout,
  getTenantSession,
  getTenantId,
  isPrimaryOccupant,
  validatePassword,
} from "./TenantAuth";
import { useTenantHeader } from "./TenantLayout";

// ─── Helpers ─────────────────────────────────────────────────
function getInitials(first = "", last = "") {
  if (first && last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  }
  const clean = (first || last || "").replace(/^(mr|mrs|ms|miss|dr|prof|engr|arch|barr|alh|alhaji)\.?\s+/i, "").trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }
  if (parts.length === 1 && parts[0].length >= 2) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (clean.slice(0, 2) || "TP").toUpperCase();
}

function fmtMoney(v) {
  if (v == null || v === "" || isNaN(v)) return "₦0";
  return new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 }).format(Number(v));
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

function StatusChip({ color = "#a1a1aa", children }) {
  return (
    <span className="d6m-chip" style={{ "--dot": color }}>
      {children}
    </span>
  );
}

function Field({ label, required, hint, error, children, className = "" }) {
  return (
    <div className={`d6m-field ${className}`}>
      <label>
        {label}
        {required && <span className="text-danger ms-1">*</span>}
      </label>
      {children}
      {error ? <div className="d6m-error">{error}</div> : hint ? <div className="d6m-hint">{hint}</div> : null}
    </div>
  );
}

function CardHead({ title, subtitle, children, className = "mb-4" }) {
  return (
    <div className={`d-flex align-items-start justify-content-between flex-wrap gap-3 ${className}`}>
      <div className="min-w-0">
        <h3 className="text-gray-900 d6-title mb-1">{title}</h3>
        {subtitle && <span className="text-gray-400 fs-8">{subtitle}</span>}
      </div>
      {children && <div className="d-flex align-items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

function PagePreloader({ visible }) {
  return (
    <div
      className={`page-preloader-overlay d6-topnav-preloader ${visible ? "is-visible" : ""}`}
      aria-hidden={!visible}
    >
      <div className="logo-box">
        <svg className="house-icon" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <path d="M 34 60 L 34 80 L 18 80 L 18 50 L 34 34 L 50 50 L 50 85" fill="none" stroke="#FFFFFF" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
          <path d="M 45.31 22.69 L 66 43.37 L 66 85" fill="none" stroke="#BBEE6B" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
          <path d="M 56.63 11.37 L 82 36.75 L 82 85" fill="none" stroke="#FFD75B" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
        </svg>
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────
export default function TenantProfile() {
  const navigate = useNavigate();
  const { setHeaderConfig } = useTenantHeader();
  const fileInputRef = useRef(null);
  const containerRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState("overview"); // overview | edit | security

  // Edit form state
  const [editForm, setEditForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    occupation: "",
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Change password state
  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [showPass, setShowPass] = useState({
    current: false,
    next: false,
    confirm: false,
  });
  const [updatingPass, setUpdatingPass] = useState(false);

  const isPo = isPrimaryOccupant();
  const tenantId = getTenantId();

  // Handle sign out
  const handleLogout = useCallback(async () => {
    try {
      await tenantLogout();
    } finally {
      navigate("/sign-in", { replace: true });
    }
  }, [navigate]);

  // Set header
  useEffect(() => {
    setHeaderConfig({
      title: "My Profile",
      breadcrumb: "Profile",
      actions: null,
    });
    return () => setHeaderConfig({ title: "", breadcrumb: "", actions: null });
  }, [setHeaderConfig]);

  // Fetch profile details
  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getProfileDetails(tenantId);
      setProfile(data);
      setEditForm({
        firstName: data?.firstName || "",
        lastName: data?.lastName || "",
        email: data?.email || "",
        phoneNumber: data?.phoneNumber || "",
        occupation: data?.occupation || "",
      });
    } catch (err) {
      console.error("Profile fetch error:", err);
      toast.error(err.message || "Failed to load profile details.");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // GSAP animation
  useEffect(() => {
    if (!loading && containerRef.current) {
      const ctx = gsap.context(() => {
        gsap.from(".d6-anim-hero", {
          opacity: 0,
          y: -15,
          duration: 0.5,
          stagger: 0.08,
          ease: "power2.out",
        });
        gsap.from(".d6-anim-card", {
          opacity: 0,
          y: 20,
          duration: 0.5,
          stagger: 0.06,
          ease: "power2.out",
          delay: 0.1,
        });
      }, containerRef);
      return () => ctx.revert();
    }
  }, [loading, activeTab]);

  // Handle image upload
  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file (JPG, PNG, WebP).");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image file size must not exceed 5MB.");
      return;
    }

    try {
      setUploadingImage(true);
      toast.loading("Uploading photo…", { id: "upload_img" });
      const imageUrl = await uploadProfileImage(file, tenantId);
      
      await updateProfile(
        {
          firstName: profile?.firstName || "",
          lastName: profile?.lastName || "",
          email: profile?.email || "",
          phoneNumber: profile?.phoneNumber || "",
          imageUrl,
          ...(isPo ? {} : { occupation: profile?.occupation || "" }),
        },
        tenantId
      );

      setProfile(prev => ({ ...prev, imageUrl }));
      toast.success("Profile photo updated!", { id: "upload_img" });
    } catch (err) {
      console.error("Upload error:", err);
      toast.error(err.message || "Failed to upload photo.", { id: "upload_img" });
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Handle profile edit save
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!editForm.firstName.trim() || !editForm.lastName.trim()) {
      toast.error("First name and last name are required.");
      return;
    }
    if (!editForm.email.trim()) {
      toast.error("Email address is required.");
      return;
    }

    try {
      setSavingProfile(true);
      await updateProfile(
        {
          ...editForm,
          imageUrl: profile?.imageUrl || "",
        },
        tenantId
      );
      toast.success("Profile updated successfully!");
      setProfile(prev => ({ ...prev, ...editForm }));
      setActiveTab("overview");
    } catch (err) {
      console.error("Save profile error:", err);
      toast.error(err.message || "Failed to update profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  // Handle password update
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!passwords.currentPassword) {
      toast.error("Please enter your current password.");
      return;
    }
    const ruleErr = validatePassword(passwords.newPassword);
    if (ruleErr) {
      toast.error(ruleErr);
      return;
    }
    if (passwords.newPassword !== passwords.confirmNewPassword) {
      toast.error("New passwords do not match.");
      return;
    }

    try {
      setUpdatingPass(true);
      await updatePassword(passwords);
      toast.success("Password updated successfully!");
      setPasswords({ currentPassword: "", newPassword: "", confirmNewPassword: "" });
      setActiveTab("overview");
    } catch (err) {
      console.error("Update password error:", err);
      toast.error(err.message || "Failed to update password.");
    } finally {
      setUpdatingPass(false);
    }
  };

  const session = getTenantSession();
  const displayName = profile?.firstName
    ? `${profile.title ? profile.title + " " : ""}${profile.firstName} ${profile.lastName || ""}`.trim()
    : session?.firstName
    ? `${session.firstName} ${session.lastName || ""}`.trim()
    : "Tenant";

  const assignedBuildings = useMemo(() => {
    return profile?.assignedBuildings || 
      (profile?.currentAssignment ? [profile.currentAssignment] : []);
  }, [profile]);

  const outstandingInvoices = useMemo(() => {
    return profile?.outstandingInvoices || [];
  }, [profile]);

  const isRentPaid = (profile?.currentRentPaymentStatus || "").toLowerCase() === "paid" || 
    (profile?.currentRentPaymentStatus || "").toLowerCase() === "active";

  return (
    <>
      <PagePreloader visible={loading && !profile} />

      <div ref={containerRef} className="app-container container-fluid py-6 px-4 px-lg-8">
        {/* ─── Hero Profile Header Banner ─── */}
        <div className="d6-card p-0 mb-6 overflow-hidden border-0 shadow-sm d6-anim-hero">
          {/* Decorative Gradient Banner */}
          <div
            style={{
              height: 120,
              background: "linear-gradient(135deg, #0d1117 0%, #0a2818 50%, #0d5c32 100%)",
              position: "relative",
            }}
          >
            <div
              style={{
                position: "absolute",
                right: 30,
                top: -30,
                width: 160,
                height: 160,
                borderRadius: "50%",
                background: "rgba(10,200,80,0.12)",
              }}
            />
          </div>

          <div className="p-5 p-lg-6 pt-0">
            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-4 flex-wrap">
              {/* Profile Avatar & Info */}
              <div className="d-flex align-items-center gap-4 flex-wrap" style={{ marginTop: "-45px" }}>
                <div className="position-relative">
                  {profile?.imageUrl ? (
                    <img
                      src={profile.imageUrl}
                      alt={displayName}
                      className="rounded-circle border border-4 border-white shadow-sm"
                      style={{ objectFit: "cover", width: 100, height: 100 }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/assets/media/zangon/pkaceholder.jpg";
                      }}
                    />
                  ) : (
                    <div
                      className="rounded-circle border border-4 border-white bg-light-success text-success fw-bold shadow-sm d-flex align-items-center justify-content-center"
                      style={{ fontSize: "2rem", width: 100, height: 100 }}
                    >
                      {getInitials(profile?.firstName, profile?.lastName)}
                    </div>
                  )}

                  {/* Camera Upload Trigger */}
                  <label
                    className="btn btn-icon btn-circle btn-success position-absolute bottom-0 end-0 shadow-sm cursor-pointer"
                    title="Upload profile photo"
                    style={{ width: 32, height: 32 }}
                  >
                    {uploadingImage ? (
                      <span className="spinner-border spinner-border-sm text-white" />
                    ) : (
                      <i className="ki-outline ki-camera fs-6 text-white" />
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="d-none"
                      onChange={handleImageChange}
                      disabled={uploadingImage}
                    />
                  </label>
                </div>

                <div className="mt-2 mt-md-0">
                  <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                    <h2 className="d6-title fs-3 text-gray-900 mb-0">{displayName}</h2>
                    <span className="badge bg-light-success text-success fs-9 fw-bold px-2.5 py-1 rounded-pill">
                      {isPo ? "Primary Occupant" : "Resident / Tenant"}
                    </span>
                    {profile?.isCorporateTenant && (
                      <span className="badge bg-light-info text-info fs-9 fw-bold px-2.5 py-1 rounded-pill">
                        Corporate
                      </span>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-4 text-gray-500 fs-8 flex-wrap">
                    {profile?.email && (
                      <span className="d-flex align-items-center gap-1.5">
                        <i className="ki-outline ki-sms fs-6 text-gray-400" />
                        {profile.email}
                      </span>
                    )}
                    {profile?.phoneNumber && (
                      <span className="d-flex align-items-center gap-1.5">
                        <i className="ki-outline ki-phone fs-6 text-gray-400" />
                        {profile.phoneNumber}
                      </span>
                    )}
                    {profile?.occupation && (
                      <span className="d-flex align-items-center gap-1.5">
                        <i className="ki-outline ki-briefcase fs-6 text-gray-400" />
                        {profile.occupation}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons & Tab Switcher */}
              <div className="d-flex align-items-center gap-2 flex-wrap ms-auto">
                <div className="d6-seg">
                  <button
                    type="button"
                    className={`d6-seg-btn ${activeTab === "overview" ? "active" : ""}`}
                    onClick={() => setActiveTab("overview")}
                  >
                    <i className="ki-outline ki-user fs-5 me-2" />
                    Overview
                  </button>
                  <button
                    type="button"
                    className={`d6-seg-btn ${activeTab === "edit" ? "active" : ""}`}
                    onClick={() => setActiveTab("edit")}
                  >
                    <i className="ki-outline ki-pencil fs-5 me-2" />
                    Edit Profile
                  </button>
                  <button
                    type="button"
                    className={`d6-seg-btn ${activeTab === "security" ? "active" : ""}`}
                    onClick={() => setActiveTab("security")}
                  >
                    <i className="ki-outline ki-shield-tick fs-5 me-2" />
                    Security
                  </button>
                </div>

                <button
                  type="button"
                  className="btn btn-light-danger d6-mbtn d-flex align-items-center gap-1.5"
                  onClick={handleLogout}
                >
                  <i className="ki-outline ki-entrance-left fs-5 me-2" />
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ─── Hero Stats Section ─── */}
        <div className="d6-kpi-brand mb-6 d6-anim-hero">
          <div className="row g-0">
            <div className="col-12 col-sm-6 col-xl-3">
              <div className="p-5 p-lg-6 h-100 border-end border-white border-opacity-15">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px symbol-circle bg-white bg-opacity-20 d-flex align-items-center justify-content-center me-4">
                    <i className="ki-outline ki-home-2 fs-2 text-white" />
                  </div>
                  <div>
                    <div className="text-white text-opacity-80 fs-8 fw-semibold text-uppercase tracking-wider">
                      Assigned Units
                    </div>
                    <div className="d6-num text-white fs-2hx fw-bold my-0.5">{assignedBuildings.length}</div>
                    <div className="text-white text-opacity-70 fs-9">Active allocations</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-xl-3">
              <div className="p-5 p-lg-6 h-100 border-end border-white border-opacity-15">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px symbol-circle bg-white bg-opacity-20 d-flex align-items-center justify-content-center me-4">
                    <i className="ki-outline ki-people fs-2 text-white" />
                  </div>
                  <div>
                    <div className="text-white text-opacity-80 fs-8 fw-semibold text-uppercase tracking-wider">
                      Dependants
                    </div>
                    <div className="d6-num text-white fs-2hx fw-bold my-0.5">{profile?.dependentCount || 0}</div>
                    <div className="text-white text-opacity-70 fs-9">Linked occupants</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-xl-3">
              <div className="p-5 p-lg-6 h-100 border-end border-white border-opacity-15">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px symbol-circle bg-white bg-opacity-20 d-flex align-items-center justify-content-center me-4">
                    <i className="ki-outline ki-bill fs-2 text-white" />
                  </div>
                  <div>
                    <div className="text-white text-opacity-80 fs-8 fw-semibold text-uppercase tracking-wider">
                      Current Rent
                    </div>
                    <div className="d6-num text-white fs-2hx fw-bold my-0.5">
                      {profile?.currentRent?.rentAmount ? fmtMoney(profile.currentRent.rentAmount) : "—"}
                    </div>
                    <div className="text-white text-opacity-70 fs-9">Term valuation</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-xl-3">
              <div className="p-5 p-lg-6 h-100">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px symbol-circle bg-white bg-opacity-20 d-flex align-items-center justify-content-center me-4">
                    <i className={`ki-outline ${isRentPaid ? "ki-check-circle" : "ki-information"} fs-2 text-white`} />
                  </div>
                  <div>
                    <div className="text-white text-opacity-80 fs-8 fw-semibold text-uppercase tracking-wider">
                      Rent Status
                    </div>
                    <div className="d6-num text-white fs-2hx fw-bold my-0.5">
                      {profile?.currentRentPaymentStatus || (outstandingInvoices.length === 0 ? "Paid" : "Pending")}
                    </div>
                    <div className="text-white text-opacity-70 fs-9">
                      {outstandingInvoices.length} outstanding invoice{outstandingInvoices.length === 1 ? "" : "s"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ─── TAB: OVERVIEW ─── */}
        {activeTab === "overview" && (
          <div className="row g-6 d6-anim-card">
            {/* Left Column: Personal Information & Rent */}
            <div className="col-12 col-xl-6">
              {/* Personal Information Card */}
              <div className="d6-card p-5 p-lg-6 mb-6">
                <CardHead
                  title="Personal Information"
                  subtitle="Your official identity and registered tenant details"
                >
                  <button
                    type="button"
                    className="btn btn-light d6-mbtn btn-sm"
                    onClick={() => setActiveTab("edit")}
                  >
                    <i className="ki-outline ki-pencil fs-5 me-2" />
                    Edit
                  </button>
                </CardHead>

                <div className="d-flex flex-column gap-3">
                  <div className="d-flex justify-content-between align-items-center p-3 rounded-2 bg-light">
                    <span className="d6m-muted fs-8">Full Legal Name</span>
                    <span className="fw-semibold text-gray-900 fs-7">{displayName}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center p-3 rounded-2 bg-light">
                    <span className="d6m-muted fs-8">Email Address</span>
                    <span className="fw-semibold text-gray-900 fs-7">{profile?.email || "—"}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center p-3 rounded-2 bg-light">
                    <span className="d6m-muted fs-8">Phone Number</span>
                    <span className="fw-semibold text-gray-900 fs-7">{profile?.phoneNumber || "—"}</span>
                  </div>
                  {!isPo && profile?.occupation && (
                    <div className="d-flex justify-content-between align-items-center p-3 rounded-2 bg-light">
                      <span className="d6m-muted fs-8">Occupation</span>
                      <span className="fw-semibold text-gray-900 fs-7">{profile.occupation}</span>
                    </div>
                  )}
                  <div className="d-flex justify-content-between align-items-center p-3 rounded-2 bg-light">
                    <span className="d6m-muted fs-8">Account Privilege</span>
                    <StatusChip color="#0EA84A">
                      {isPo ? "Primary Occupant" : "Resident"}
                    </StatusChip>
                  </div>
                  {profile?.estateId && (
                    <div className="d-flex justify-content-between align-items-center p-3 rounded-2 bg-light">
                      <span className="d6m-muted fs-8">Estate Resident ID</span>
                      <span className="font-monospace text-gray-800 fw-bold fs-8">{profile.estateId}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Current Rent Card */}
              {profile?.currentRent && (
                <div className="d6-card p-5 p-lg-6">
                  <CardHead
                    title="Current Rent Overview"
                    subtitle="Active leasing rate and term schedule"
                  />
                  <div className="d6m-invoice">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <div className="d6m-muted fs-8">Current Term Rent</div>
                        <div className="d6m-amount text-success">{fmtMoney(profile.currentRent.rentAmount)}</div>
                      </div>
                      <StatusChip color={isRentPaid ? "#0EA84A" : "#DD6B20"}>
                        {profile.currentRentPaymentStatus || "Active"}
                      </StatusChip>
                    </div>

                    <div className="d6m-lines">
                      <div className="d6m-line">
                        <span>Effective Schedule From</span>
                        <span className="fw-semibold text-gray-900">{fmtDate(profile.currentRent.effectiveFrom)}</span>
                      </div>
                      {profile.currentRent.effectiveTo && (
                        <div className="d6m-line">
                          <span>Expiry / Renewal Date</span>
                          <span className="fw-semibold text-gray-900">{fmtDate(profile.currentRent.effectiveTo)}</span>
                        </div>
                      )}
                      <div className="d6m-line d6m-line-total">
                        <span>Total Registered Rate</span>
                        <span className="text-success">{fmtMoney(profile.currentRent.rentAmount)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column: Properties & Outstanding Invoices */}
            <div className="col-12 col-xl-6">
              {/* Assigned Properties */}
              <div className="d6-card p-5 p-lg-6 mb-6">
                <CardHead
                  title="Assigned Units &amp; Properties"
                  subtitle={`${assignedBuildings.length} active allocation${assignedBuildings.length === 1 ? "" : "s"}`}
                />

                {assignedBuildings.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <i className="ki-outline ki-home fs-2x mb-2 d-block text-gray-300" />
                    No properties currently assigned.
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {assignedBuildings.map((b, idx) => (
                      <div
                        key={b.assignmentId || b.buildingId || idx}
                        className="d6-list-card p-3.5 d-flex align-items-center justify-content-between flex-wrap gap-3"
                      >
                        <div className="d-flex align-items-center gap-3">
                          <div className="symbol symbol-40px symbol-circle bg-light-success text-success d-flex align-items-center justify-content-center">
                            <i className="ki-outline ki-home-2 fs-4 text-success" />
                          </div>
                          <div>
                            <span className="d6m-name d-block">
                              {b.number ? `House ${b.number}` : (b.buildingName || "Assigned Residence")}
                            </span>
                            <span className="d6m-muted fs-8">
                              {b.phaseName || "Estate"} • {b.typeName || "Residential"}
                            </span>
                          </div>
                        </div>

                        {b.assignedAt && (
                          <div className="text-end">
                            <span className="d6m-muted fs-9 d-block">Assigned</span>
                            <span className="fs-8 fw-semibold text-gray-800">{fmtDate(b.assignedAt)}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Outstanding Invoices */}
              <div className="d6-card p-5 p-lg-6">
                <CardHead
                  title="Outstanding Invoices"
                  subtitle="Pending rent and utility payment schedules"
                />

                {outstandingInvoices.length === 0 ? (
                  <div className="d-flex flex-column align-items-center justify-content-center py-8 text-center">
                    <div className="symbol symbol-50px symbol-circle bg-light-success text-success d-flex align-items-center justify-content-center mb-2">
                      <i className="ki-outline ki-shield-tick fs-2tx text-success" />
                    </div>
                    <div className="fs-7 fw-bold text-gray-800">All invoices are settled!</div>
                    <div className="fs-8 text-gray-400">No overdue balances on your property account.</div>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {outstandingInvoices.map((inv, idx) => (
                      <div
                        key={inv.id || idx}
                        className="d6-list-card p-3.5 d-flex align-items-center justify-content-between flex-wrap gap-3"
                      >
                        <div>
                          <div className="fs-6 fw-bold text-danger">
                            {fmtMoney(inv.rentAmount || inv.amount)}
                          </div>
                          <div className="d6m-muted fs-8">
                            Due: {fmtDate(inv.dueDate)}
                          </div>
                        </div>
                        <StatusChip color="#E53E3E">
                          {inv.status || "Pending"}
                        </StatusChip>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ─── TAB: EDIT PROFILE ─── */}
        {activeTab === "edit" && (
          <div className="d6-card p-5 p-lg-7 d6-anim-card" style={{ maxWidth: 840, margin: "0 auto" }}>
            <CardHead
              title="Edit Profile Information"
              subtitle="Update your contact details and account credentials."
            />

            <form onSubmit={handleProfileSubmit}>
              <div className="row g-4 mb-5">
                <div className="col-12 col-md-6">
                  <Field label="First Name" required hint="Your legal given name">
                    <input
                      type="text"
                      className="d6m-input"
                      value={editForm.firstName}
                      onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                      required
                    />
                  </Field>
                </div>

                <div className="col-12 col-md-6">
                  <Field label="Last Name" required hint="Your legal surname">
                    <input
                      type="text"
                      className="d6m-input"
                      value={editForm.lastName}
                      onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                      required
                    />
                  </Field>
                </div>

                <div className="col-12 col-md-6">
                  <Field label="Email Address" required hint="Official email for receipts &amp; notifications">
                    <input
                      type="email"
                      className="d6m-input"
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      required
                    />
                  </Field>
                </div>

                <div className="col-12 col-md-6">
                  <Field label="Phone Number" required hint="Phone in international format (+234...)">
                    <input
                      type="tel"
                      className="d6m-input"
                      placeholder="+234 800 000 0000"
                      value={editForm.phoneNumber}
                      onChange={(e) => setEditForm({ ...editForm, phoneNumber: e.target.value })}
                      required
                    />
                  </Field>
                </div>

                {!isPo && (
                  <div className="col-12">
                    <Field label="Occupation" hint="e.g. Software Engineer, Business Executive">
                      <input
                        type="text"
                        className="d6m-input"
                        placeholder="e.g. Legal Practitioner, Architect"
                        value={editForm.occupation}
                        onChange={(e) => setEditForm({ ...editForm, occupation: e.target.value })}
                      />
                    </Field>
                  </div>
                )}
              </div>

              <div className="d-flex align-items-center justify-content-between pt-4 border-top">
                <button
                  type="button"
                  className="btn btn-light d6-mbtn"
                  onClick={() => setActiveTab("overview")}
                  disabled={savingProfile}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-success d6-mbtn"
                  disabled={savingProfile}
                >
                  {savingProfile ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving Changes…
                    </>
                  ) : (
                    <>
                      <i className="ki-outline ki-check-circle fs-5 me-2.5" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ─── TAB: CHANGE PASSWORD / SECURITY ─── */}
        {activeTab === "security" && (
          <div className="d6-card p-5 p-lg-7 d6-anim-card" style={{ maxWidth: 680, margin: "0 auto" }}>
            <CardHead
              title="Security &amp; Password"
              subtitle="Update your resident login password."
            />

            <form onSubmit={handlePasswordSubmit}>
              <div className="d-flex flex-column gap-4 mb-5">
                {/* Current Password */}
                <Field label="Current Password" required hint="Enter your existing password to verify identity">
                  <div className="position-relative">
                    <input
                      type={showPass.current ? "text" : "password"}
                      className="d6m-input pe-12"
                      placeholder="Enter current password"
                      value={passwords.currentPassword}
                      onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
                      required
                    />
                    <button
                      type="button"
                      className="btn d6-icon-btn position-absolute top-50 translate-middle-y end-0 me-2"
                      onClick={() => setShowPass({ ...showPass, current: !showPass.current })}
                      style={{ width: 34, height: 34 }}
                    >
                      <i className={`ki-outline ${showPass.current ? "ki-eye-slash" : "ki-eye"} fs-5 text-gray-500`} />
                    </button>
                  </div>
                </Field>

                {/* New Password */}
                <Field label="New Password" required hint="Create a strong password with at least 6 characters">
                  <div className="position-relative">
                    <input
                      type={showPass.next ? "text" : "password"}
                      className="d6m-input pe-12"
                      placeholder="Enter new password"
                      value={passwords.newPassword}
                      onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                      required
                    />
                    <button
                      type="button"
                      className="btn d6-icon-btn position-absolute top-50 translate-middle-y end-0 me-2"
                      onClick={() => setShowPass({ ...showPass, next: !showPass.next })}
                      style={{ width: 34, height: 34 }}
                    >
                      <i className={`ki-outline ${showPass.next ? "ki-eye-slash" : "ki-eye"} fs-5 text-gray-500`} />
                    </button>
                  </div>
                </Field>

                {/* Confirm New Password */}
                <Field label="Confirm New Password" required hint="Re-enter your new password">
                  <div className="position-relative">
                    <input
                      type={showPass.confirm ? "text" : "password"}
                      className="d6m-input pe-12"
                      placeholder="Confirm new password"
                      value={passwords.confirmNewPassword}
                      onChange={(e) => setPasswords({ ...passwords, confirmNewPassword: e.target.value })}
                      required
                    />
                    <button
                      type="button"
                      className="btn d6-icon-btn position-absolute top-50 translate-middle-y end-0 me-2"
                      onClick={() => setShowPass({ ...showPass, confirm: !showPass.confirm })}
                      style={{ width: 34, height: 34 }}
                    >
                      <i className={`ki-outline ${showPass.confirm ? "ki-eye-slash" : "ki-eye"} fs-5 text-gray-500`} />
                    </button>
                  </div>
                </Field>

                {/* Password Rules Card */}
                <div className="d6-card p-3.5 bg-light border border-gray-200">
                  <div className="fs-8 fw-bold text-gray-800 mb-2">Password Requirements:</div>
                  <ul className="fs-8 text-gray-600 mb-0 ps-4 d-flex flex-column gap-1">
                    <li className={passwords.newPassword.length >= 6 ? "text-success fw-bold" : ""}>
                      At least 6 characters in length
                    </li>
                    <li className={/[A-Z]/.test(passwords.newPassword) ? "text-success fw-bold" : ""}>
                      At least 1 uppercase letter (A-Z)
                    </li>
                    <li className={/[0-9]/.test(passwords.newPassword) ? "text-success fw-bold" : ""}>
                      At least 1 numeric digit (0-9)
                    </li>
                    <li className={/[-!@#$%^&*(),.?":{}|<>]/.test(passwords.newPassword) ? "text-success fw-bold" : ""}>
                      At least 1 special symbol (!@#$...)
                    </li>
                  </ul>
                </div>
              </div>

              <div className="d-flex align-items-center justify-content-between pt-4 border-top">
                <button
                  type="button"
                  className="btn btn-light d6-mbtn"
                  onClick={() => setActiveTab("overview")}
                  disabled={updatingPass}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-success d6-mbtn"
                  disabled={updatingPass}
                >
                  {updatingPass ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Updating Password…
                    </>
                  ) : (
                    <>
                      <i className="ki-outline ki-key fs-5 me-2.5" />
                      Update Password
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </>
  );
}
